import { BadgeCheck, Brain } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { FactList } from "./FactList";
import { usePhoneContext } from "./usePhoneContext";

/**
 * What we already know about the number in front of the operator.
 *
 * The same facts the co-pilot puts in its own panel, one screen earlier and with
 * no AI attached: our scan of their site, what the register says, what is open
 * on their account. None of it needs a model, a key or a switch, and until this
 * existed none of it was readable unless the AI panel was open on a live call.
 *
 * SILENT WHEN THERE IS NOTHING TO SAY, and that is the rule that makes it usable
 * on a dial pad. Most numbers on a cold list resolve to nobody, and a panel that
 * announced "no information" on every one of them would be a permanent block of
 * grey text under the field, teaching the operator to stop reading the space
 * where the useful version appears. Loading is silent for the same reason: it
 * lasts a moment and precedes an answer that is usually empty.
 */
export function PhoneContextPanel({
  number,
  enabled = true,
  className,
  onAddToNote,
}: {
  /** The dialable number, E.164, or null while what is typed is not one yet. */
  number: string | null;
  enabled?: boolean;
  className?: string;
  /** Mid-call only: there is a note to put a fact into. */
  onAddToNote?: (text: string) => void;
}) {
  const t = useT();
  const { context } = usePhoneContext(number, enabled);

  // `hadContact` is worth a line on its own even with no facts behind it: "we
  // have spoken to them before" changes the opening sentence, and it is the one
  // thing here that is true of a company with no site and no register row.
  const hasFacts = (context?.facts.length ?? 0) > 0;
  if (!context || (!hasFacts && !context.hadContact && !context.scanUncertain)) return null;

  // The brain's lines are a MODEL's reading, and everything else in this list
  // is something a scanner or a registry measured. The badge beside each row
  // says which, but a badge is a symbol somebody has to have been taught - and
  // this panel is read by an operator about to repeat these sentences to the
  // company they are about. Shown only when a brain line is actually present,
  // so a briefing with no model output in it does not carry a warning about
  // model output.
  const hasBrain = context.facts.some((fact) => fact.source === "brain");

  return (
    // Its own strings rather than the co-pilot's, even where the sentence is
    // nearly the same. The panel's own two lines say "suggestions come from
    // earlier conversations" - which names a thing that does not exist at a dial
    // pad and re-attaches the whole feature to the AI this file exists to
    // detach it from. The facts are the same; the sentence around them is not.
    <section className={cn("w-full", className)}>
      <h4 className="crm-cp-h">{t("calls.context.title")}</h4>
      {hasFacts ? (
        <FactList facts={context.facts} onAddToNote={onAddToNote} />
      ) : (
        <p className="mt-1.5 text-[12px] leading-[1.55] text-muted-foreground">
          {t(context.scanUncertain ? "calls.context.scanUncertain" : "calls.context.noFacts")}
        </p>
      )}
      <p className="crm-cp-track">
        <BadgeCheck className="size-3" />
        {t(context.hadContact ? "calls.context.known" : "calls.context.cold")}
      </p>
      {hasBrain ? (
        <p className="crm-cp-track">
          <Brain className="size-3" />
          {t("calls.context.brainNote")}
        </p>
      ) : null}
    </section>
  );
}
